import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../layout/header/Header';
import Footer from '../../layout/footer/Footer';
import {
  fetchStoreProductById,
  fetchStoreProducts,
  getEffectivePrice,
  getFallbackProductImage,
  resolveProductImage,
  type StoreProduct,
} from '../../data/productsApi';
import { GlobalContext } from '../../store/GlobalContext';
import api from '../../api/axios';
import { addToGuestCart, getGuestCartCount } from '../../utils/guestCart';
// @ts-ignore Bundler-resolved side-effect stylesheet import.
import './Description.css';

const formatCurrency = (value: number) => `PKR ${Math.round(value).toLocaleString()}`;

const Description = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, setCartCount } = useContext(GlobalContext);
  const token = state.auth.token;
  const isLoggedIn = Boolean(token);
  const [product, setProduct] = useState<StoreProduct | null>(null);
  const [allProducts, setAllProducts] = useState<StoreProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    let mounted = true;

    const loadProduct = async () => {
      setLoading(true);
      setError('');
      try {
        const [selected, products] = await Promise.all([
          fetchStoreProductById(id),
          fetchStoreProducts(100),
        ]);

        if (!mounted) return;
        setProduct(selected);
        setAllProducts(products);
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.response?.data?.message || 'Product not found. Please select another product.');
        setProduct(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, [id]);

  const relatedProducts = useMemo(() => {
    if (!product) {
      return allProducts.slice(0, 4);
    }

    const sameCategory = allProducts.filter(
      (item) => item._id !== product._id && item.categoryId === product.categoryId,
    );

    if (sameCategory.length >= 4) {
      return sameCategory.slice(0, 4);
    }

    const fallback = allProducts.filter((item) => item._id !== product._id);
    return [...sameCategory, ...fallback].slice(0, 4);
  }, [product, allProducts]);

  const galleryImages = useMemo(() => {
    if (!product?.images?.length) {
      const fallback = getFallbackProductImage();
      return [fallback, fallback, fallback];
    }

    const normalized = product.images.map((image) => resolveProductImage(image));
    while (normalized.length < 3) {
      normalized.push(normalized[0]);
    }
    return normalized;
  }, [product]);

  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [addingToCart, setAddingToCart] = useState(false);

  const handleAddToCart = async (): Promise<boolean> => {
    if (!product) return false;
    setAddingToCart(true);
    const effectivePrice = getEffectivePrice(product);
    const image = product.images?.[0] ? resolveProductImage(product.images[0]) : getFallbackProductImage();

    try {
      if (isLoggedIn) {
        await api.post(
          '/cart/my/items',
          { productId: product._id, quantity },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const res = await api.get('/cart/my', { headers: { Authorization: `Bearer ${token}` } });
        const totalItems = Number(res.data?.totalItems ?? 0);
        setCartCount(totalItems);
        setNote(`Added ${quantity} x "${product.name}" to your cart.`);
        return true;
      }

      addToGuestCart({
        productId: String(product._id),
        name: product.name,
        price: effectivePrice,
        stock: Number(product.stock ?? 99),
        image,
        quantity,
      });
      setCartCount(getGuestCartCount());
      setNote(`Added ${quantity} x "${product.name}" to your cart.`);
      return true;
    } catch (err: any) {
      setNote(err?.response?.data?.message || 'Could not add to cart. Please try again.');
      return false;
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    const added = await handleAddToCart();
    if (added) {
      navigate('/checkout');
    }
  };

  useEffect(() => {
    setActiveImage(0);
  }, [product?._id]);

  const productCategory = product?.category?.name || 'All Products';
  const productPrice = product ? getEffectivePrice(product) : 0;

  if (loading) {
    return (
      <>
        <Header />
        <section className="arkab-detail-page">
          <Container className="arkab-detail-shell">
            <Alert variant="light" className="mb-4">Loading product...</Alert>
          </Container>
        </section>
        <Footer />
      </>
    );
  }

  if (!product || error) {
    return (
      <>
        <Header />
        <section className="arkab-detail-page">
          <Container className="arkab-detail-shell">
            <Alert variant="warning" className="mb-4">{error || 'Product not found. Please select a product from All Products.'}</Alert>
            <Button variant="dark" onClick={() => navigate('/products')}>Back to Products</Button>
          </Container>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      <section className="arkab-detail-page">
        <Container className="arkab-detail-shell">
          <div className="arkab-detail-breadcrumb">
            <button type="button" onClick={() => navigate('/')}>Home</button>
            <span>/</span>
            <button type="button" onClick={() => navigate('/products')}>All Products</button>
            <span>/</span>
            <strong>{product.name}</strong>
          </div>

          <Row className="g-4 g-lg-5 align-items-start">
            <Col lg={7}>
              <div className="arkab-detail-media-card">
                <div className="arkab-detail-thumbs" role="tablist" aria-label="Product images">
                  {galleryImages.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      className={activeImage === index ? 'active' : ''}
                      onClick={() => setActiveImage(index)}
                      aria-label={`Preview ${index + 1}`}
                    >
                      <img src={image} alt={`${product.name} preview ${index + 1}`} loading="lazy" />
                    </button>
                  ))}
                </div>

                <div className="arkab-detail-main-image-wrap">
                  <img src={galleryImages[activeImage] || getFallbackProductImage()} alt={product.name} className="arkab-detail-main-image" />
                  {Number(product.discount || 0) > 0 ? (
                    <Badge bg="danger" className="arkab-detail-discount-badge">-{product.discount}%</Badge>
                  ) : null}
                </div>
              </div>
            </Col>

            <Col lg={5}>
              <div className="arkab-detail-info-card">
                <div className="arkab-detail-category-chip">{productCategory}</div>
                <h1>{product.name}</h1>
                <p className="arkab-detail-sku">SKU: {product.sku || product._id.toUpperCase()}</p>

                <div className="arkab-detail-price-stack">
                  {Number(product.discount || 0) > 0 ? (
                    <span className="arkab-detail-old-price">{formatCurrency(product.price)}</span>
                  ) : null}
                  <strong>{formatCurrency(productPrice)}</strong>
                  <span className="arkab-detail-tax">Taxes included. Shipping calculated at checkout.</span>
                </div>

                <p className="arkab-detail-description">
                  {product.description || 'Crafted for everyday elegance with premium finish, timeless style, and comfortable wear.'}
                </p>

                <div className="arkab-detail-qty-wrap">
                  <span>Quantity</span>
                  <div className="arkab-detail-qty-controls">
                    <button type="button" onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}>-</button>
                    <strong>{quantity}</strong>
                    <button type="button" onClick={() => setQuantity((prev) => Math.min(10, prev + 1))}>+</button>
                  </div>
                </div>

                <div className="arkab-detail-actions">
                  <Button variant="outline-dark" disabled={addingToCart} onClick={handleAddToCart}>Add to cart</Button>
                  <Button variant="dark" disabled={addingToCart} onClick={handleBuyNow}>Buy Now</Button>
                </div>

                {note ? <Alert variant="light" className="mt-3 mb-0">{note}</Alert> : null}

                <div className="arkab-detail-highlights">
                  <div>
                    <h3>Highlights</h3>
                    <ul>
                      <li>Premium finishing and polished detailing</li>
                      <li>Elegant gifting-ready design</li>
                      <li>Comfortable all-day wear profile</li>
                    </ul>
                  </div>

                  <div>
                    <h3>Shipping & Returns</h3>
                    <ul>
                      <li>Fast delivery in all major cities</li>
                      <li>Easy returns policy on eligible products</li>
                      <li>Support available Monday to Saturday</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <section className="arkab-related-section">
            <div className="arkab-related-head">
              <h2>All Products</h2>
              <Button variant="link" onClick={() => navigate('/products')}>View all</Button>
            </div>

            <Row className="g-3 g-md-4">
              {relatedProducts.map((item) => {
                const image = item.images?.[0] ? resolveProductImage(item.images[0]) : getFallbackProductImage();
                const price = getEffectivePrice(item);
                return (
                  <Col key={item._id} xs={6} md={4} lg={3}>
                    <article className="arkab-related-card" onClick={() => navigate(`/products/${item._id}`)}>
                      <img src={image} alt={item.name} loading="lazy" />
                      <div className="arkab-related-card-body">
                        <p>{item.category?.name || 'Featured'}</p>
                        <h3>{item.name}</h3>
                        <strong>{formatCurrency(price)}</strong>
                      </div>
                    </article>
                  </Col>
                );
              })}
            </Row>
          </section>

          <section className="arkab-help-strip">
            <h2>Need help with your order?</h2>
            <p>Our support team is available for sizing guidance, product checks, and delivery updates.</p>
            <Form className="arkab-help-form" onSubmit={(event) => event.preventDefault()}>
              <Form.Control type="email" placeholder="Enter your email for support updates" />
              <Button type="submit" variant="dark">Get Support</Button>
            </Form>
          </section>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default Description;
